import React, { useMemo, useState } from 'react';
import { inject, observer } from 'mobx-react';
import classnames from 'classnames';
import { useTheme } from '@fluentui/react';
import { getTheme, Text, FontWeights, FontSizes, mergeStyleSets, DefaultButton } from '@fluentui/react';

import { useBrandedLogos } from 'src/brands';
import MainLogo from 'src/v2/components/base/MainLogo';
import InviteForm from '../InviteForm';

const theme = getTheme();
const classNames = mergeStyleSets({
  root: {
    display: 'flex',
    minHeight: '100vh',
    flexDirection: 'column',
    flex: '1 auto',
    position: 'relative',
    boxSizing: 'border-box',
  },
  header: {
    width: '100%',
  },
  content: {
    overflow: 'auto',
    flex: '1 auto',
    // height: 'calc(100vh - 46px)' // To pin the footer
  },
  footer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    flexWrap: 'nowrap',
    padding: '8px 12px 0px 12px',
    height: 50,
    borderTop: '1px solid #F5F5F5',
    flex: '0 0 50px',
    boxSizing: 'border-box',
    selectors: {
      '& .logo': {
        width: 100,
        height: 30,
      },
    },
  },

  licenseStatus: [
    theme.fonts.small,
    {
      textAlign: 'center',
      fontWeight: FontWeights.semilight
    },
  ],
  
  inviteButton: {
    height: 30,
    padding: '5px 10px',
    fontSize: FontSizes.medium,
    background: '#f4f4f4',
    outline: 'none',
    border: 'none',
    ':hover': {
      background: '#EDEBE9'
    }
  }
})

const MainDecorator = ({ children, className, userStore, isSidePanel = false }) => {
  const theme = useTheme();
  const { hasTrueFreePlan, hasTrueTrialPlan, remainingDays, canSeeLicenseInformation, plan, originalPlan } = userStore.license;
  const planText = `${originalPlan} ${hasTrueTrialPlan || hasTrueFreePlan ? ' version' : ''}`;
  const remainingText = hasTrueTrialPlan ? `. ${remainingDays} ${remainingDays === 1 ? 'day' : 'days'} left` : '';
  const text = userStore.isAuthorized && canSeeLicenseInformation ? `${planText}${remainingText}` : '';

  const [inviteFormOpened, setInviteFormOpened] = useState(false);

  const { smallLogo, smallLightLogo } = useBrandedLogos();

  const classNamesMerged = useMemo(() => mergeStyleSets(
    classNames, {
      footer: {
        selectors: {
          '& .logo': {
            background: `url(${isSidePanel ? smallLightLogo : smallLogo}) no-repeat center center`,
            backgroundSize: 'contain',
          }
        }
      },

      licenseTextColor: {
        color: isSidePanel ? 'white' : theme.fonts.small.color,
      },
    }
  ), [isSidePanel, theme, smallLogo, smallLightLogo]);

  const rootStyle = useMemo(() => isSidePanel ? {} : { minWidth: '310px' }, [isSidePanel]);

  return (
    <div className={classnames(classNames.root, className)} style={rootStyle}>
      {!userStore.showPageFooter &&
        <div className={classNames.header}>
          <MainLogo logo={smallLogo} dimensions={{ width: '50%', height: 50 }}>
            {text && <Text className={classnames(classNames.licenseStatus, classNamesMerged.licenseTextColor)}>{text}</Text>}
          </MainLogo>
        </div>}
      <div className={classNames.content}>
        {children}
      </div>
      {userStore.showPageFooter && <>
        <div className={classNamesMerged.footer}>
          <div className="logo" />
          {!isSidePanel && text && <>
            <Text className={classnames(classNames.licenseStatus, classNamesMerged.licenseTextColor)}>{text}</Text>
            <DefaultButton
              className={classNames.inviteButton}
              text="Invite a friend"
              onClick={() => setInviteFormOpened(true)}
              styles={{
                label: {
                  textWrap: 'nowrap',
                  fontWeight: FontWeights.semibold
                }
              }}
            />
          </>}
        </div>
        <InviteForm isOpen={inviteFormOpened} onDismiss={() => setInviteFormOpened(false)} />
      </>
      }
    </div>
  );
}

export default inject('userStore')(observer(MainDecorator));